import { designTokens } from '../design-system/tokens';

// Type definitions for better type safety
export type ColorScale = keyof typeof designTokens.colors.brand.primary;
export type SemanticColorType = keyof typeof designTokens.colors.semantic;
export type BrandColorType = keyof typeof designTokens.colors.brand;
export type NeutralColorType = keyof typeof designTokens.colors.neutral;

/**
 * Get a semantic color value
 */
export const getSemanticColor = (
  color: SemanticColorType,
  scale: ColorScale = 500,
): string => designTokens.colors.semantic[color][scale];

/**
 * Get a brand color value
 */
export const getBrandColor = (
  color: BrandColorType,
  scale: ColorScale = 500,
): string => designTokens.colors.brand[color][scale];

/**
 * Get a neutral color value
 */
export const getNeutralColor = (scale: NeutralColorType): string => designTokens.colors.neutral[scale];

/**
 * Get surface color value
 */
export const getSurfaceColor = (type: keyof typeof designTokens.surface): string => designTokens.surface[type];

/**
 * Convert hex color to RGB values for CSS custom properties
 */
export const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    throw new Error(`Invalid hex color: ${hex}`);
  }

  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);

  return `${r} ${g} ${b}`;
};

/**
 * Create CSS custom property value from hex color
 */
export const createCssVar = (hex: string): string => `rgb(${hexToRgb(hex)})`;

/**
 * Get theme-aware color based on current theme
 */
export const getThemeAwareColor = (
  lightColor: string,
  darkColor: string,
  isDark = false,
): string => (isDark ? darkColor : lightColor);

/**
 * Generate color palette for a given base color
 */
export const generateColorPalette = (baseColor: string) => {
  // This is a simplified version - you might want to use a library like 'chroma-js' for better results
  const base = baseColor.replace('#', '');
  const r = parseInt(base.substr(0, 2), 16);
  const g = parseInt(base.substr(2, 2), 16);
  const b = parseInt(base.substr(4, 2), 16);

  const palette: Record<string, string> = {};

  // Generate lighter shades (50-400)
  for (let i = 50; i <= 400; i += 50) {
    const factor = 1 - (i / 500);
    const newR = Math.round(r + (255 - r) * (1 - factor));
    const newG = Math.round(g + (255 - g) * (1 - factor));
    const newB = Math.round(b + (255 - b) * (1 - factor));
    palette[i.toString()] = `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  }

  // Base color (500)
  palette['500'] = baseColor;

  // Generate darker shades (600-950)
  for (let i = 600; i <= 950; i += 50) {
    const factor = (i - 500) / 500;
    const newR = Math.round(r * (1 - factor));
    const newG = Math.round(g * (1 - factor));
    const newB = Math.round(b * (1 - factor));
    palette[i.toString()] = `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  }

  return palette;
};

/**
 * Tailwind class helpers for consistent usage
 */
export const tw = {
  // Background utilities
  bg: {
    primary: 'bg-surface-base',
    secondary: 'bg-surface-secondary',
    tertiary: 'bg-surface-tertiary',
    accent: 'bg-surface-accent',
    success: 'bg-success-500',
    warning: 'bg-warning-500',
    error: 'bg-error-500',
    info: 'bg-info-500',
  },

  // Text utilities
  text: {
    primary: 'text-text-primary',
    secondary: 'text-text-secondary',
    tertiary: 'text-text-tertiary',
    inverse: 'text-text-inverse',
    success: 'text-success-600',
    warning: 'text-warning-600',
    error: 'text-error-600',
    info: 'text-info-600',
  },

  // Border utilities
  border: {
    primary: 'border-border-primary',
    secondary: 'border-border-secondary',
    success: 'border-success-300',
    warning: 'border-warning-300',
    error: 'border-error-300',
    info: 'border-info-300',
  },

  // Ring utilities for focus states
  ring: {
    primary: 'ring-primary-500',
    success: 'ring-success-500',
    warning: 'ring-warning-500',
    error: 'ring-error-500',
    info: 'ring-info-500',
  },
} as const;

/**
 * Component variant helpers
 */
export const getButtonVariant = (variant: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'ghost' = 'primary') => {
  const variants = {
    primary: 'bg-primary-500 hover:bg-primary-600 text-white border-primary-500',
    secondary: 'bg-secondary-500 hover:bg-secondary-600 text-white border-secondary-500',
    success: 'bg-success-500 hover:bg-success-600 text-white border-success-500',
    warning: 'bg-warning-500 hover:bg-warning-600 text-white border-warning-500',
    error: 'bg-error-500 hover:bg-error-600 text-white border-error-500',
    ghost: 'bg-transparent hover:bg-surface-secondary text-text-primary border-border-primary',
  };

  return variants[variant];
};

/**
 * Status color helpers
 */
export const getStatusColor = (status: 'success' | 'warning' | 'error' | 'info' | 'neutral') => {
  const statusColors = {
    success: {
      bg: 'bg-success-50',
      text: 'text-success-700',
      border: 'border-success-200',
      icon: 'text-success-500',
    },
    warning: {
      bg: 'bg-warning-50',
      text: 'text-warning-700',
      border: 'border-warning-200',
      icon: 'text-warning-500',
    },
    error: {
      bg: 'bg-error-50',
      text: 'text-error-700',
      border: 'border-error-200',
      icon: 'text-error-500',
    },
    info: {
      bg: 'bg-info-50',
      text: 'text-info-700',
      border: 'border-info-200',
      icon: 'text-info-700',
    },
    neutral: {
      bg: 'bg-neutral-50',
      text: 'text-neutral-700',
      border: 'border-neutral-200',
      icon: 'text-neutral-700',
    },
  };

  return statusColors[status];
};
